import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import type { ModulePropType } from '@rio-cloud/rio-uikit/ApplicationHeader';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { ConfigCatProvider, createConsoleLogger, DataGovernance, PollingMode } from 'configcat-react';
import { lazy, Suspense } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { Outlet } from 'react-router-dom';

import { NavigationLink } from '@/components/NavigationLink';
import { config } from '@/config';
import { DEFAULT_LOCALE } from '@/configuration/lang/lang';

// Lazy load components to reduce bundle size
const LazyAppHeader = lazy(() => import('@/features/header/AppHeader'));
const LazyGuidedTourModal = lazy(() =>
    import('@/features/guidedTour/components/Modal').then(m => ({ default: m.GuidedTourModal }))
);
const LazyOrderNewCards = lazy(() =>
    import('@/features/orderNewCards/OrderNewCards').then(m => ({ default: m.OrderNewCards }))
);
const LazyUserManualModal = lazy(() =>
    import('@/features/userManual/components/UserManualModal').then(m => ({ default: m.UserManualModal }))
);
const LazyWhatsNewInitializer = lazy(() => import('@/features/userManual/components/WhatsNew/WhatsNewInitializer'));
const LazyWhatsNewBottomSheet = lazy(() => import('@/features/userManual/components/WhatsNew/WhatsNewBottomSheet'));

import { ModalContextProvider } from '@/hooks/useModal';
import { includesAny } from '@/lib/utils';
import DefaultRedirect from '@/routes/DefaultRedirect';
import {
    ADMINISTRATION_CARDS,
    ADMINISTRATION_CONTRACT,
    CHARGING_ACTIVITY,
    DEFAULT_ROUTE,
    INVOICES,
} from '@/routes/Router';
import { useAccountStore } from '@/stores/accountStore';
import { useAppStore } from '@/stores/appStore';
import { useLocaleStore } from '@/stores/localeStore';
import { useUserStore } from '@/stores/userStore';

export interface NavItemType extends ModulePropType {
    permissions: string[];
}

const AppLayout = () => {
    const { displayLocale, displayMessages } = useLocaleStore();
    const { userSessionExpired } = useAccountStore();
    const { sessionExpiredAcknowledged, hideSessionExpiredDialog } = useAppStore();
    const showSessionExpired = userSessionExpired && !sessionExpiredAcknowledged;
    const { isEligible, account } = useUserStore();
    const permissions = account?.permissions;
    if (!(displayMessages && displayLocale)) {
        return null;
    }

    const defaultAdministrationRoute = permissions?.includes('emsp-cards.read')
        ? ADMINISTRATION_CARDS
        : permissions?.includes('emsp-contract.read')
          ? ADMINISTRATION_CONTRACT
          : DEFAULT_ROUTE;

    const navItems: NavItemType[] = [
        {
            key: 'overview',
            permissions: ['emsp-dashboard'],
            route: (
                <NavigationLink to={DEFAULT_ROUTE}>
                    <FormattedMessage id='general.mainNavigation.overview' />
                </NavigationLink>
            ),
        },
        {
            key: 'charging',
            permissions: ['emsp-charging-sessions.read', 'emsp-invoices.read'],
            route: (
                <NavigationLink to={CHARGING_ACTIVITY}>
                    <FormattedMessage id='general.mainNavigation.chargingActivity' />
                </NavigationLink>
            ),
        },
        {
            key: 'invoices',
            permissions: ['emsp-invoices.read'],
            route: (
                <NavigationLink to={INVOICES}>
                    <FormattedMessage id='general.subNavigation.invoices' />
                </NavigationLink>
            ),
        },
        {
            key: 'administration',
            permissions: ['emsp-cards.read', 'emsp-contract.read'],
            route: (
                <NavigationLink to={defaultAdministrationRoute}>
                    <FormattedMessage id='general.mainNavigation.administration' />
                </NavigationLink>
            ),
        },
    ];

    const accountIsOnboarded = account?.isOnboarded;
    const allowedNavItems =
        isEligible && accountIsOnboarded ? navItems.filter(item => includesAny(permissions, item.permissions)) : [];

    const customOnErrorHandler = (error: { code: string }) => {
        if (error.code === 'MISSING_TRANSLATION' || error.code === 'FORMAT_ERROR') {
            return null;
        }
        console.error(error);
    };

    const canSeeGuidedTour = permissions?.includes('emsp-invoices.read');
    const logger = createConsoleLogger(config.configCat.logLevel);

    const renderContent = () => {
        if (!permissions) {
            const NoPermissionsStatePage = lazy(() => import('@/pages/NoPermissions'));
            return (
                <Suspense fallback={null}>
                    <NoPermissionsStatePage />
                </Suspense>
            );
        }
        if (!isEligible) {
            const NotBookedStatePage = lazy(() => import('@/pages/NotBooked'));
            return (
                <Suspense fallback={null}>
                    <NotBookedStatePage />
                </Suspense>
            );
        }
        return <Outlet />;
    };

    return (
        <IntlProvider
            defaultLocale={DEFAULT_LOCALE}
            key={displayLocale}
            locale={displayLocale}
            messages={displayMessages}
            onError={customOnErrorHandler}
        >
            <ConfigCatProvider
                sdkKey={config.configCat.sdkKey}
                pollingMode={PollingMode.LazyLoad}
                options={{
                    cacheTimeToLiveSeconds: config.configCat.cacheTimeInSeconds,
                    logger,
                    dataGovernance: DataGovernance.EuOnly,
                }}
            >
                <ModalContextProvider>
                    <ApplicationLayout data-testid='app-layout'>
                        <ApplicationLayout.Header>
                            <Suspense fallback={null}>
                                <LazyAppHeader navItems={allowedNavItems} />
                            </Suspense>
                        </ApplicationLayout.Header>
                        <NotificationsContainer />
                        <SessionExpiredDialog
                            locale={displayLocale}
                            onClose={hideSessionExpiredDialog}
                            show={showSessionExpired}
                        />
                        {renderContent()}
                        <DefaultRedirect />
                        {accountIsOnboarded && (
                            <>
                                {canSeeGuidedTour && (
                                    <Suspense fallback={null}>
                                        <LazyGuidedTourModal />
                                    </Suspense>
                                )}
                                <Suspense fallback={null}>
                                    <LazyOrderNewCards />
                                </Suspense>
                                <Suspense fallback={null}>
                                    <LazyUserManualModal />
                                </Suspense>
                                <Suspense fallback={null}>
                                    <LazyWhatsNewInitializer />
                                </Suspense>
                                <Suspense fallback={null}>
                                    <LazyWhatsNewBottomSheet />
                                </Suspense>
                            </>
                        )}
                    </ApplicationLayout>
                </ModalContextProvider>
            </ConfigCatProvider>
        </IntlProvider>
    );
};

export default AppLayout;
